<script>
import options from "./NykCard.options"

export default {
    ...options
}
</script>

<template>
    <article :class="outerClasses" :aria-label="ariaLabel || undefined">
        <div :class="innerClasses">
            <!-- Media section (image/video) -->
            <div v-if="hasMediaSlot || mediaSrc" class="nyk-generic-card__inner__media">
                <slot name="media">
                    <img v-if="type === 'image' && mediaSrc" 
                        :src="mediaSrc" 
                        :alt="mediaAriaLabel" 
                        class="nyk-generic-card__inner__media-image" />
                    <video v-else-if="type === 'video' && mediaSrc"
                        ref="videoElement"
                        class="nyk-generic-card__inner__media-video"
                        controls
                        :aria-label="mediaAriaLabel"
                        :tabindex="thumbnailVisible ? -1 : 0">
                        <source :src="mediaSrc" type="video/mp4" />
                    </video>
                    
                    <!-- Video thumbnail overlay -->
                    <Transition name="nyk-transition-fade-out" @after-leave="onAfterLeave">
                        <div v-if="showThumbnail && thumbnailVisible" 
                            class="nyk-generic-card__inner__media-thumbnail"
                            :style="{ backgroundImage: `url(${videoThumbnailUrl})` }"
                            role="img"
                            :aria-label="`Video thumbnail: ${mediaAriaLabel || title}`">
                            <div class="nyk-generic-card__inner__media-thumbnail__footer">
                                <NykButton
                                    as="button"
                                    variant="neutral"
                                    shape="square"
                                    :animate-icon="false"
                                    :aria-label="`${mediaAriaLabel || 'Play video'}: ${title}`"
                                    @click="playVideo">
                                    <template #icon>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                            <path d="M8 5v14l11-7L8 5z" fill="currentColor"/>
                                        </svg>
                                    </template>
                                </NykButton>
                                <NykButton
                                    variant="hyperlink-neutral"
                                    justify-content="start"
                                    @click="playVideo">
                                    {{ mediaAriaLabel }}
                                </NykButton>
                            </div>
                        </div>
                    </Transition>
                </slot>
            </div>

            <div class="nyk-generic-card__inner__content">
            <!-- Meta info section (above title) -->
            <div v-if="hasMeta" class="nyk-generic-card__inner__content__meta">
                <slot name="meta">
                    <div class="nyk-generic-card__inner__content__meta__info">
                        <NykTag 
                            v-if="metaTag"
                            :title="metaTag"
                            :variant="computedTagVariant"
                            :size="tagSize"
                            @close="handleTagClose" />
                        <span v-if="metaCategory" class="nyk-generic-card__inner__content__meta__category">
                            {{ metaCategory }}
                        </span>
                        <span v-if="metaDate" class="nyk-generic-card__inner__content__meta__date">
                            {{ metaDate }}
                        </span>
                    </div>
                </slot>
            </div>

            <!-- Header section -->
            <div v-if="hasHeaderSlot || title" class="nyk-generic-card__inner__content__header">
                <slot name="header">
                    <h3 v-if="title" class="nyk-generic-card__inner__content__title">{{ title }}</h3>
                </slot>
            </div>

            <!-- Contact info section -->
            <dl v-if="hasContactInfo" class="nyk-generic-card__inner__content__contact-info">
                <div v-if="phone" class="nyk-generic-card__inner__content__contact-info__item">
                    <dt class="nyk-generic-card__inner__content__contact-info__item__label">{{ phoneLabel }}</dt>
                    <dd>
                        <a :href="`tel:${phone.replace(/\s/g, '')}`"
                            :aria-label="`${phoneLabel}: ${phone}`"
                            class="nyk-generic-card__inner__content__contact-info__item__value">{{ phone }}</a>
                    </dd>
                </div>
                <div v-if="email" class="nyk-generic-card__inner__content__contact-info__item">
                    <dt class="nyk-generic-card__inner__content__contact-info__item__label">{{ emailLabel }}</dt>
                    <dd>
                        <a :href="`mailto:${email.replace(/\s/g, '')}`"
                            :aria-label="`${emailLabel}: ${email}`"
                            class="nyk-generic-card__inner__content__contact-info__item__value">{{ email }}</a>
                    </dd>
                </div>
            </dl>

            <!-- Social section -->
            <div v-if="hasSocial" class="nyk-generic-card__inner__content__social">
                <NykButton
                    v-if="linkedInUrl"
                    as="link"
                    :href="linkedInUrl"
                    target="_blank"
                    :variant="socialButtonVariant"
                    shape="round"
                    :animate-icon="false"
                    aria-label="LinkedIn profile (opens in new tab)">
                    <template #icon> 
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452z" fill="currentColor"/>
                        </svg>
                    </template>
                </NykButton>
                <NykButton
                    v-if="twitterUrl"
                    as="link"
                    :href="twitterUrl"
                    target="_blank"
                    :variant="socialButtonVariant"
                    shape="round"
                    :animate-icon="false"
                    aria-label="Twitter/X profile (opens in new tab)">
                    <template #icon>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                            <path d="M15.751 2h2.851l-6.227 7.117L19.5 18h-5.733l-4.491-5.87L4.023 18H1.17l6.66-7.614L1.5 2h5.88l4.058 5.366L15.751 2zm-1 14.354h1.58L6.328 3.609H4.64l10.111 12.745z" fill="currentColor"/>
                        </svg>
                    </template>
                </NykButton>
                <NykButton
                    v-if="facebookUrl"
                    as="link"
                    :href="facebookUrl"
                    target="_blank"
                    :variant="socialButtonVariant"
                    shape="round"
                    :animate-icon="false"
                    aria-label="Facebook profile (opens in new tab)">
                    <template #icon>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="currentColor"/>
                        </svg>
                    </template>
                </NykButton>
            </div>

            <!-- Main content area -->
            <div v-if="hasContentSlot || description" class="nyk-generic-card__inner__content__description">
                <slot>
                    <p v-if="description" class="nyk-generic-card__inner__content__description__text" v-html="description"></p>
                </slot>
            </div>

            <!-- Footer section -->
            <div v-if="hasFooterSlot || footerLinkText || footerLinkHref" class="nyk-generic-card__inner__content__footer">
                <slot name="footer">
                    <NykButton
                        v-if="footerLinkText && footerLinkHref"
                        as="link"
                        :href="footerLinkHref"
                        :target="footerLinkTarget"
                        :variant="hyperlinkVariant"
                        @click="handleLinkClick">
                        {{ footerLinkText }}
                    </NykButton>
                    <NykButton
                        v-if="footerLinkHref"
                        as="link"
                        :href="footerLinkHref"
                        :target="footerLinkTarget"
                        :aria-label="footerLinkAriaLabel || footerLinkText || 'View more'"
                        variant="default"
                        shape="round"
                        :animate-icon="true"
                        @click="handleLinkClick">
                        <template #icon>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 12" width="14" height="12" role="presentation" aria-hidden="true">
                                <path fill="var(--colortheme-icon)" d="M0 5.393v1.231h11.37l-3.898 4.243.67.633L14 5.991 8.142.5l-.67.633 3.898 4.26H0Z"></path>
                            </svg>
                        </template>
                    </NykButton>
                </slot>
            </div>
        </div>
        </div>
    </article>
</template>
